import type { QuickBookingMode } from '../booking/BookingCreateContext';
import {
  isoToCalendarDayKey,
  isPastCalendarDayKey,
  parseCalendarDayKey,
  todayCalendarDayKey,
  toCalendarDayKey,
} from './assetBookingCalendar';
import { countBookingPeriodsBetween } from './bookingTotalPriceCompute';
import {
  defaultHourlyStartTime,
  formatLocalHoursMinutes,
} from './bookingScheduleDefaults';
import {
  buildDailyStartEndIso,
  buildLocalStartEndIso,
} from './openingHoursBookingValidate';

function compareHm(a: string, b: string): number {
  return a.localeCompare(b);
}

function addHoursToHm(hm: string, hours: number): string {
  const [hh, mm] = hm.split(':').map((x) => parseInt(x, 10));
  const h = Number.isFinite(hh) ? hh : 0;
  const min = Number.isFinite(mm) ? mm : 0;
  const d = new Date(2000, 0, 1, h, min, 0, 0);
  d.setHours(d.getHours() + hours);
  return formatLocalHoursMinutes(d);
}

export function endDayKeyForDailyCount(startDayKey: string, count: number): string | null {
  const parsed = parseCalendarDayKey(startDayKey);
  if (!parsed || count < 1) {
    return null;
  }
  const end = new Date(parsed.year, parsed.monthIndex, parsed.day + count - 1);
  return toCalendarDayKey(end.getFullYear(), end.getMonth(), end.getDate());
}

export function endDayKeyForMonthlyCount(startDayKey: string, count: number): string | null {
  const parsed = parseCalendarDayKey(startDayKey);
  if (!parsed || count < 1) {
    return null;
  }
  const boundary = new Date(parsed.year, parsed.monthIndex + count, parsed.day);
  boundary.setDate(boundary.getDate() - 1);
  return toCalendarDayKey(boundary.getFullYear(), boundary.getMonth(), boundary.getDate());
}

/**
 * Recompute start/end when checkout took long enough that the saved instants are stale.
 * Keeps the same billing period count for day/month bookings.
 */
export function refreshStaleQuickBookingSchedule(
  startIso: string,
  endIso: string,
  mode: QuickBookingMode,
  now: Date = new Date(),
): { startIso: string; endIso: string } | null {
  const startKey = isoToCalendarDayKey(startIso);
  const endKey = isoToCalendarDayKey(endIso);
  if (!startKey || !endKey) {
    return null;
  }

  if (mode === 'hour') {
    const startDt = new Date(startIso.trim());
    const endDt = new Date(endIso.trim());
    if (Number.isNaN(startDt.getTime()) || Number.isNaN(endDt.getTime())) {
      return null;
    }
    const parsed = parseCalendarDayKey(startKey);
    if (!parsed) {
      return null;
    }
    let dayKey = startKey;
    let { year, monthIndex, day } = parsed;
    if (isPastCalendarDayKey(startKey, now)) {
      dayKey = todayCalendarDayKey(now);
      const todayParsed = parseCalendarDayKey(dayKey);
      if (!todayParsed) {
        return null;
      }
      ({ year, monthIndex, day } = todayParsed);
    }
    const minStart = defaultHourlyStartTime(year, monthIndex, day, now);
    let startHm = formatLocalHoursMinutes(startDt);
    if (dayKey !== startKey) {
      startHm = minStart;
    } else if (compareHm(startHm, minStart) < 0) {
      startHm = minStart;
    }
    let endHm = formatLocalHoursMinutes(endDt);
    if (compareHm(endHm, startHm) <= 0) {
      endHm = addHoursToHm(startHm, 1);
    }
    return buildLocalStartEndIso(dayKey, startHm, endHm);
  }

  const periodCount = countBookingPeriodsBetween(startIso, endIso, mode);
  const count = periodCount !== null && periodCount >= 1 ? periodCount : 1;
  let useStartKey = startKey;
  if (isPastCalendarDayKey(startKey, now)) {
    useStartKey = todayCalendarDayKey(now);
  }
  const useEndKey =
    mode === 'month'
      ? endDayKeyForMonthlyCount(useStartKey, count)
      : endDayKeyForDailyCount(useStartKey, count);
  if (!useEndKey) {
    return null;
  }
  return buildDailyStartEndIso(useStartKey, useEndKey, now);
}
